import { useEffect, useRef, useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useUI } from "@/context/UIContext";
import { usePermissions } from "@/lib/permissions";
import { geofenceService, vehicleService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { KPICard } from "@/components/common/KPICard";
import { MapCanvas } from "@/components/common/MapCanvas";
import { SkeletonMap } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { EditGeofenceDialog } from "@/components/dialogs/EditGeofenceDialog";
import { GeofenceScheduleFields } from "@/components/dialogs/GeofenceScheduleFields";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Hexagon, Circle, Plus, Trash2, Pencil, MapPin, Car, Activity, LogIn, LogOut, CalendarClock, Move, X, Check } from "lucide-react";

const DRAFT_EMPTY = {
  name: "", type: null, shape: null, // shape: {centerLat,centerLng,radiusM} | {coordinates:[[lat,lng],...]}
  alertOnEnter: true, alertOnExit: true, activeDays: null, activeStartTime: "", activeEndTime: "",
};

export default function Geofences() {
  const { facilities } = useUI();
  const { isManager } = usePermissions();
  const { data: fences, loading, refetch } = useFetch(() => geofenceService.list(), []);
  const { data: vehicles } = useFetch(() => vehicleService.list(), []);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [draft, setDraft] = useState(DRAFT_EMPTY);
  const [creating, setCreating] = useState(false);
  // Drawing happens on this page's own map (no separate dialog) — `drawing`
  // is the mode toggle, `drawTool` is the active shape tool, and `drawnShape`
  // holds the live geometry MapCanvas reports back (it re-reports on every
  // edit, so this stays current if the shape is nudged before saving).
  const [drawing, setDrawing] = useState(false);
  const [drawTool, setDrawTool] = useState(null);
  const [drawnShape, setDrawnShape] = useState(null);
  const [polyPoints, setPolyPoints] = useState(0); // in-progress polygon corners placed
  const [drawPhase, setDrawPhase] = useState(null); // null | "sizing" (circle) | "corners" (polygon)
  const [finishSignal, setFinishSignal] = useState(0); // bumped to close the polygon
  // The map's root element while it is fullscreen, else null. Held rather than
  // a bare boolean because dialogs opened over a fullscreened map have to be
  // portalled INTO that element — nothing outside it gets painted.
  const [fsElement, setFsElement] = useState(null);
  const mapFullscreen = !!fsElement;
  const [createError, setCreateError] = useState(null);
  // Stands in for a toast while fullscreen: the app's toaster is mounted at
  // the root of the page tree, which the browser stops painting the moment
  // the map goes fullscreen.
  const [flash, setFlash] = useState(null);
  const flashTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(flashTimerRef.current), []);

  const flashOnMap = (message) => {
    clearTimeout(flashTimerRef.current);
    setFlash(message);
    flashTimerRef.current = setTimeout(() => setFlash(null), 3500);
  };

  // Geofence id currently being re-pushed to Bouncie, so only that card's
  // badge shows a spinner rather than all of them.
  const [syncing, setSyncing] = useState(null);

  const retrySync = async (g) => {
    setSyncing(g.id);
    try {
      await geofenceService.syncToBouncie(g.id);
      // The push runs in a worker, so the row comes back "pending" — say what
      // actually happened rather than claiming it is synced.
      toast.success("Re-sending this zone to Bouncie");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not queue the sync");
    } finally {
      setSyncing(null);
    }
  };

  const active = (fences || []).filter((g) => g.status === "active").length;
  const inside = (fences || []).reduce((s, g) => s + (g.vehiclesInside || 0), 0);
  const breaches = (fences || []).reduce((s, g) => s + g.recentActivity.length, 0);

  const startDrawing = () => {
    setSelected(null);
    setDrawnShape(null);
    setDrawTool("circle"); // pre-arm the common case rather than making it an extra click
    setDrawing(true);
  };

  const cancelDrawing = () => {
    setDrawing(false);
    setDrawTool(null);
    setDrawnShape(null);
    setPolyPoints(0);
    setDrawPhase(null);
    setDraft(DRAFT_EMPTY);
  };

  const confirmShape = () => {
    if (!drawnShape) return;
    setCreateError(null);
    setDraft((f) => ({ ...f, type: drawnShape.type, shape: drawnShape }));
    setCreateOpen(true);
  };

  const create = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      await geofenceService.create({
        name: draft.name || "New Geofence", type: draft.type,
        centerLat: draft.shape?.centerLat, centerLng: draft.shape?.centerLng, radiusM: draft.shape?.radiusM,
        coordinates: draft.shape?.coordinates,
        alertOnEnter: draft.alertOnEnter, alertOnExit: draft.alertOnExit,
        activeDays: draft.activeDays, activeStartTime: draft.activeStartTime, activeEndTime: draft.activeEndTime,
      });
      setCreateOpen(false);
      setDraft(DRAFT_EMPTY);
      setDrawing(false);
      setDrawTool(null);
      setDrawnShape(null);
      setPolyPoints(0);
      setDrawPhase(null);
      toast.success("Geofence created");
      flashOnMap("Geofence created");
      refetch();
    } catch (err) {
      // Inline, not a toast: this dialog's own overlay sits above the toaster,
      // and in fullscreen the toaster is not painted at all — either way the
      // reason a save failed has to appear inside the dialog.
      setCreateError(err.response?.data?.error || "Could not create geofence");
    } finally {
      setCreating(false);
    }
  };

  const drawHint = drawnShape
    ? `${drawnShape.type === "circle"
        ? `Circle · ~${drawnShape.radiusM} m radius`
        : `Polygon · ${drawnShape.coordinates.length} points`} · drag to adjust`
    : drawTool === "polygon"
      ? (polyPoints === 0
        ? "Click the map to place each corner."
        : `${polyPoints} corner${polyPoints === 1 ? "" : "s"} placed${polyPoints < 3 ? ` · ${3 - polyPoints} more needed` : " · double-click or press Finish"}`)
      : drawTool === "circle"
        ? (drawPhase === "sizing"
          ? "Move the mouse to size it, then click again to finish."
          : "Click on the map to place the circle's center.")
        : "Pick Circle or Polygon above to start drawing.";

  // Everything layered over the map. Passed to MapCanvas as its `overlay`
  // rather than rendered as a sibling so it goes fullscreen WITH the map — as
  // siblings, the draw tools and the Done button vanished the moment the map
  // was expanded, which made the whole flow unusable in fullscreen.
  const mapOverlay = (
    <>
      {drawing && (
        <>
          {/* Shape tools, over the map — same place Bouncie puts them.
              Sits one row below the map's place-search box rather than on
              top of it: searching for an address and *then* drawing the
              geofence there is the normal flow, so both need to be
              reachable at once. */}
          <div className="absolute left-3 top-14 z-10 flex gap-1 rounded-xl border border-border/70 bg-card/95 p-1 shadow-lg backdrop-blur">
            <DrawTool icon={Move} label="Pan" active={drawTool === null} onClick={() => setDrawTool(null)} testId="draw-tool-pan" />
            <DrawTool icon={Circle} label="Circle" active={drawTool === "circle"} onClick={() => { setDrawnShape(null); setDrawTool("circle"); }} testId="draw-tool-circle" />
            <DrawTool icon={Hexagon} label="Polygon" active={drawTool === "polygon"} onClick={() => { setDrawnShape(null); setDrawTool("polygon"); }} testId="draw-tool-polygon" />
          </div>

          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-border/70 bg-card/95 px-3 py-2 shadow-lg backdrop-blur">
            <span className="text-xs text-muted-foreground">{drawHint}</span>
            {drawnShape ? (
              <Button size="sm" onClick={confirmShape} data-testid="draw-done">
                <Check className="mr-1.5 h-3.5 w-3.5" /> Done
              </Button>
            ) : drawTool === "polygon" ? (
              <Button size="sm" disabled={polyPoints < 3} onClick={() => setFinishSignal((n) => n + 1)} data-testid="draw-finish-polygon">
                <Check className="mr-1.5 h-3.5 w-3.5" /> Finish
              </Button>
            ) : null}
            {/* In fullscreen the page header is off-screen, so the way out of
                draw mode has to be reachable on the map itself. */}
            {mapFullscreen && (
              <Button variant="secondary" size="sm" onClick={cancelDrawing} data-testid="cancel-geofence-draw-fullscreen">
                <X className="mr-1.5 h-3.5 w-3.5" /> Cancel
              </Button>
            )}
          </div>
        </>
      )}

      {/* The header's Create button is invisible in fullscreen, so mirror it
          onto the map. Only in fullscreen — otherwise the page would show
          two identical Create buttons. */}
      {!drawing && mapFullscreen && isManager && (
        <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-border/70 bg-card/95 px-3 py-2 shadow-lg backdrop-blur">
          {flash && <span className="text-xs text-emerald-400">{flash}</span>}
          <Button size="sm" onClick={startDrawing} data-testid="create-geofence-fullscreen">
            <Plus className="mr-1.5 h-4 w-4" /> Create Geofence
          </Button>
        </div>
      )}
    </>
  );

  const toggle = async (g) => { await geofenceService.update(g.id, { status: g.status === "active" ? "disabled" : "active" }); refetch(); };
  const remove = async () => { await geofenceService.remove(deleteTarget.id); setDeleteTarget(null); if (selected?.id === deleteTarget.id) setSelected(null); toast.success("Geofence deleted"); refetch(); };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Geofences"
        subtitle="Define virtual boundaries and monitor vehicle entry and exit."
        actions={
          drawing ? (
            <Button variant="secondary" onClick={cancelDrawing} data-testid="cancel-geofence-draw">
              <X className="mr-1.5 h-4 w-4" /> Cancel
            </Button>
          ) : isManager ? (
            <Button onClick={startDrawing} data-testid="create-geofence">
              <Plus className="mr-1.5 h-4 w-4" /> Create Geofence
            </Button>
          ) : null
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPICard label="Total Geofences" value={fences?.length ?? "—"} icon={Hexagon} />
        <KPICard label="Active" value={active} icon={Activity} />
        <KPICard label="Vehicles Inside" value={inside} icon={Car} />
        <KPICard label="Recent Breaches" value={breaches} icon={MapPin} tone={breaches === 0 ? "success" : "danger"} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="relative h-[520px]">
          {/* Skeleton only on the FIRST load. Using a bare `loading` here
              swapped the map out for a skeleton on every refetch — and every
              create/toggle/delete/edit calls refetch — which unmounted
              MapCanvas, destroyed the Google Map, and rebuilt it at the
              default center/zoom, throwing away wherever the user had panned
              to. LiveMap/Dashboard already guard this way for the same reason
              (they poll). */}
          {loading && !fences ? <SkeletonMap /> : (
            <MapCanvas
              vehicles={vehicles || []} geofences={fences || []} facilities={facilities}
              selectedId={selected?.id} className="h-full" testId="geofence-map" viewportKey="geofences"
              drawingActive={drawing} drawingMode={drawTool} finishSignal={finishSignal}
              onShapeDrawn={(shape) => { setDrawnShape(shape); setDrawTool(null); setPolyPoints(0); setDrawPhase(null); }}
              onDrawProgress={({ pointCount, phase }) => { setPolyPoints(pointCount); setDrawPhase(phase); }}
              overlay={mapOverlay}
              onFullscreenChange={({ element }) => setFsElement(element)}
            />
          )}

        </div>
        <div className="space-y-3">
          {!loading && (fences || []).length === 0 && (
            <EmptyState icon={Hexagon} title="No geofences yet" description="Create one to start monitoring vehicle entry and exit around a yard, depot or customer site." />
          )}
          {(fences || []).map((g) => {
            const hasSchedule = g.activeDays !== null || g.activeStartTime || g.activeEndTime;
            return (
              <Card key={g.id} onClick={() => setSelected(g)} className={cn("cursor-pointer border-border/70 bg-card/50 p-4 transition hover:border-border", selected?.id === g.id && "border-primary/40")} data-testid={`geofence-${g.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border", g.status === "active" ? "border-red-500/20 bg-red-500/10 text-red-600 dark:text-red-400" : "border-border/60 bg-secondary/40 text-muted-foreground")}>
                      {g.type === "circle" ? <Circle className="h-4 w-4" /> : <Hexagon className="h-4 w-4" />}
                    </div>
                    <div>
                      <div className="font-medium">{g.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{g.type} · {g.location}</div>
                    </div>
                  </div>
                  <Switch checked={g.status === "active"} disabled={!isManager} onCheckedChange={() => toggle(g)} onClick={(e) => e.stopPropagation()} data-testid={`geofence-toggle-${g.id}`} />
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <div className="flex gap-4 text-muted-foreground">
                    <span>{g.type === "circle" ? `${g.radius} m radius` : `${g.area} km²`}</span>
                    <span>{g.assignedVehicles} vehicles</span>
                    <span className="text-emerald-400">{g.vehiclesInside} inside</span>
                  </div>
                  {isManager && (
                    <div className="flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setEditTarget(g); }} className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid={`geofence-edit-${g.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(g); }} className="rounded-md p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400" data-testid={`geofence-delete-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]", g.alertOnEnter ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300" : "border-border/60 text-muted-foreground")}>
                    <LogIn className="h-3 w-3" /> Enter
                  </span>
                  <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]", g.alertOnExit ? "border-amber-400/20 bg-amber-400/10 text-amber-700 dark:text-amber-300" : "border-border/60 text-muted-foreground")}>
                    <LogOut className="h-3 w-3" /> Exit
                  </span>
                  {hasSchedule && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-sky-400/20 bg-sky-400/10 px-2 py-0.5 text-[11px] text-sky-700 dark:text-sky-300">
                      <CalendarClock className="h-3 w-3" /> Scheduled
                    </span>
                  )}
                  <span
                    className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
                      g.recentActivity.length === 0
                        ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300"
                        : "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300")}
                    data-testid={`geofence-breaches-${g.id}`}
                  >
                    <MapPin className="h-3 w-3" /> {g.recentActivity.length === 0 ? "No breaches" : `${g.recentActivity.length} breach${g.recentActivity.length === 1 ? "" : "es"}`}
                  </span>
                  <BouncieSyncBadge geofence={g} onRetry={() => retrySync(g)} retrying={syncing === g.id} />
                </div>
                {selected?.id === g.id && g.recentActivity.length > 0 && (
                  <div className="mt-3 space-y-1.5 border-t border-border/50 pt-3">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Recent activity</div>
                    {g.recentActivity.map((a, i) => (
                      <div key={i} className="flex items-center justify-between text-xs"><span>{a.vehicle} <span className="text-muted-foreground">{a.action.toLowerCase()}</span></span><span className="text-muted-foreground">{relativeTime(a.time)}</span></div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Step 2 (step 1 is drawing on the map above): name it and set alert
          and schedule rules for the shape just drawn. */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-geofence-dialog" portalContainer={fsElement}>
          <DialogHeader>
            <DialogTitle>Name this Geofence</DialogTitle>
            <DialogDescription>Set a name and alert rules for the shape you just drew.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/20 p-3 text-sm">
              <span className="flex items-center gap-2 text-muted-foreground">
                {draft.type === "circle" ? <Circle className="h-4 w-4" /> : <Hexagon className="h-4 w-4" />}
                {draft.type === "circle"
                  ? `Circle · ~${draft.shape?.radiusM ?? 0} m radius`
                  : `Polygon · ${draft.shape?.coordinates?.length ?? 0} points`}
              </span>
              <button
                type="button"
                onClick={() => { setCreateOpen(false); setDrawnShape(null); setDrawTool(draft.type || "circle"); }}
                className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                data-testid="geofence-redraw"
              >
                <Pencil className="h-3 w-3" /> Redraw
              </button>
            </div>
            <div className="space-y-1.5"><Label>Name</Label><Input value={draft.name} onChange={(e) => setDraft((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. West Loading Zone" data-testid="geofence-name-input" /></div>
            <GeofenceScheduleFields form={draft} setForm={setDraft} />
          </div>
          <DialogFooter>
            {createError && (
              <p className="mr-auto self-center text-xs text-red-600 dark:text-red-400" data-testid="geofence-create-error">{createError}</p>
            )}
            {/* Closes this step but stays in draw mode with the shape still
                on the map, so Cancel here means "back to the map", not
                "throw away what I drew". */}
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Back</Button>
            <Button onClick={create} disabled={creating} data-testid="geofence-create-submit">{creating ? "Creating…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete geofence?"
        description={`This will permanently remove "${deleteTarget?.name}" and its activity history.`}
        confirmLabel="Delete"
        destructive
        onConfirm={remove}
      />

      <EditGeofenceDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        geofence={editTarget}
        onSaved={refetch}
      />
    </div>
  );
}

function DrawTool({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      title={label}
      data-testid={testId}
      className={cn(
        "flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition",
        active ? "bg-primary/15 text-primary" : "text-foreground/70 hover:bg-secondary hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

// Whether Bouncie is watching this zone, and what to do when it isn't.
//
// Worth a badge because the difference is not cosmetic. A synced zone is
// evaluated by Bouncie against the raw position stream and reports a crossing
// by webhook within seconds, carrying the crossing's own timestamp. An
// unsynced one is only caught by our five-minute containment pass, which
// cannot see a vehicle that entered and left between two runs. Without this,
// a zone silently in that weaker state looks identical to a working one.
const SYNC_STATES = {
  synced: {
    label: "Live on Bouncie", icon: Check,
    className: "border-emerald-400/20 bg-emerald-400/10 text-emerald-700 dark:text-emerald-300",
    title: "Bouncie is watching this zone and reports crossings within seconds.",
  },
  pending: {
    label: "Syncing…", icon: Activity,
    className: "border-sky-400/20 bg-sky-400/10 text-sky-700 dark:text-sky-300",
    title: "Being sent to Bouncie. Local checks cover the zone meanwhile.",
  },
  error: {
    label: "Not synced", icon: X,
    className: "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300",
    title: "Bouncie rejected or could not receive this zone.",
  },
  disabled: {
    label: "Local only", icon: MapPin,
    className: "border-border/60 text-muted-foreground",
    title: "Turned off, so Bouncie is not watching it. Nothing to sync.",
  },
  unavailable: {
    label: "Bouncie not connected", icon: X,
    className: "border-amber-400/20 bg-amber-400/10 text-amber-700 dark:text-amber-300",
    title: "This organization has no Bouncie connection, so zones cannot be pushed. Local checks still run every five minutes.",
  },
};

function BouncieSyncBadge({ geofence, onRetry, retrying }) {
  const state = SYNC_STATES[geofence.bouncieSyncState] || SYNC_STATES.pending;
  const Icon = retrying ? Activity : state.icon;
  // Only "error" is worth a retry button: pending is already in flight,
  // disabled is deliberate, and unavailable needs the Bouncie connection
  // fixed in Settings rather than another push attempt.
  const canRetry = geofence.bouncieSyncState === "error";
  const title = geofence.bouncieSyncError
    ? `${state.title}\n\n${geofence.bouncieSyncError}`
    : state.title;

  return (
    <span
      title={title}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]", state.className)}
      data-testid={`geofence-sync-${geofence.id}`}
    >
      <Icon className={cn("h-3 w-3", retrying && "animate-pulse")} />
      {retrying ? "Sending…" : state.label}
      {canRetry && !retrying && (
        <button
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          className="ml-0.5 underline decoration-dotted underline-offset-2 hover:no-underline"
          data-testid={`geofence-sync-retry-${geofence.id}`}
        >
          retry
        </button>
      )}
    </span>
  );
}
